import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Sparkles, Check, ArrowRight, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function Sponsor() {
  const [pkgs, setPkgs] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/sponsorships/packages").then(({ data }) => setPkgs(data)); }, []);

  const checkout = async (package_id) => {
    if (!name || !email) {
      toast.error("Add your name and email first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/sponsorships/checkout", {
        package_id, sponsor_name: name, sponsor_email: email,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-aurora text-white px-6 md:px-12 py-10 max-w-6xl mx-auto" data-testid="sponsor-page">
      <Link to="/" className="font-display text-xl font-black"><span className="text-gradient-pink">S</span><span className="text-neoncyan">0</span><span className="text-gradient-pink">rted</span></Link>

      <div className="text-center max-w-2xl mx-auto mt-10">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-neoncyan/15 border border-neoncyan/30 text-neoncyan text-xs font-bold uppercase tracking-widest"><Sparkles size={12}/> For Brands</div>
        <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter mt-5">
          Reach <span className="text-gradient-pink">young Aussies</span><br/> when they're learning to adult.
        </h1>
        <p className="text-white/70 mt-5 text-lg">
          Sponsor a challenge. Brands like Westpac, Headspace and ATO show up in real moments —
          when users are budgeting, job-hunting, moving out. Direct, contextual, measurable.
        </p>
      </div>

      <div className="glass-card p-6 mt-10 max-w-2xl mx-auto">
        <h2 className="font-display text-lg font-bold mb-4">Your details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Brand / company name"
            data-testid="sponsor-name-input"
            className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 outline-none focus:border-neonpink"/>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Contact email"
            data-testid="sponsor-email-input"
            className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 outline-none focus:border-neonpink"/>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
        {pkgs.map((p, i) => {
          const featured = i === 1;
          return (
            <div key={p.package_id} className={`glass-card p-7 flex flex-col ${featured ? "border-neonpink/40 shadow-glow" : ""}`}
              data-testid={`pkg-${p.package_id}`}>
              {featured && <div className="self-start text-xs font-bold px-3 py-1 rounded-full bg-neonpink text-white mb-3">MOST POPULAR</div>}
              <div className="font-display text-2xl font-black">{p.label}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display text-5xl font-black text-gradient-cyan">${Math.round(p.price_aud)}</span>
                <span className="text-white/50">AUD</span>
              </div>
              <div className="text-sm text-white/65 mt-2">{p.description}</div>
              <ul className="mt-5 space-y-2 text-sm text-white/85 flex-1">
                <li className="flex gap-2"><Check size={16} className="text-neongreen flex-shrink-0 mt-0.5"/> {p.challenges} sponsored {p.challenges === 1 ? "challenge" : "challenges"}</li>
                <li className="flex gap-2"><Check size={16} className="text-neongreen flex-shrink-0 mt-0.5"/> Logo on challenge card</li>
                <li className="flex gap-2"><Check size={16} className="text-neongreen flex-shrink-0 mt-0.5"/> Reach 16–30 demographic</li>
                {p.package_id !== "starter" && <li className="flex gap-2"><Check size={16} className="text-neongreen flex-shrink-0 mt-0.5"/> Engagement analytics</li>}
                {p.package_id === "platinum" && <li className="flex gap-2"><Check size={16} className="text-neongreen flex-shrink-0 mt-0.5"/> Custom landing page</li>}
              </ul>
              <button onClick={() => checkout(p.package_id)} disabled={busy} data-testid={`sponsor-buy-${p.package_id}`}
                className={`mt-6 ${featured ? "btn-primary-neon" : "btn-secondary-neon"} w-full flex items-center justify-center gap-2 disabled:opacity-50`}>
                {busy ? "Loading..." : <>Sponsor now <ArrowRight size={14}/></>}
              </button>
            </div>
          );
        })}
      </div>

      <div className="glass-card p-8 mt-10 max-w-3xl mx-auto text-center">
        <Building2 className="mx-auto text-neoncyan" size={28}/>
        <h3 className="font-display text-xl font-bold mt-3">Want a custom partnership?</h3>
        <p className="text-white/60 mt-2 text-sm">For multi-state campaigns, exclusive categories, or government programs — get in touch.</p>
        <a href="mailto:partners@adulting101.com" className="btn-secondary-neon mt-4 inline-block text-sm" data-testid="sponsor-contact">partners@adulting101.com</a>
      </div>

      <div className="text-center mt-8 text-xs text-white/40">
        Test mode: payments use Stripe test cards (4242 4242 4242 4242).
      </div>
    </div>
  );
}

export function SponsorSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState({ payment_status: "checking", status: "checking" });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      navigate("/sponsor");
      return;
    }
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled || n >= 8) return;
      try {
        const { data } = await api.get(`/sponsorships/status/${sessionId}`);
        setStatus(data);
        if (data.payment_status === "paid" || data.status === "expired") return;
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.warn("Sponsor status poll failed, retrying:", err);
        }
      }
      setAttempts(n + 1);
      setTimeout(() => poll(n + 1), 2500);
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, navigate]);

  const paid = status.payment_status === "paid";
  return (
    <div className="min-h-screen bg-aurora flex items-center justify-center p-6" data-testid="sponsor-success">
      <div className="glass-card-strong p-10 max-w-lg w-full text-center">
        {paid ? (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-neongreen/20 flex items-center justify-center text-neongreen mb-4">
              <Check size={32}/>
            </div>
            <h1 className="font-display text-3xl font-black tracking-tight">Payment received!</h1>
            <p className="text-white/70 mt-2">Thanks for sponsoring. Our team will be in touch within 1 business day to set up your branded challenge.</p>
            <p className="text-xs text-white/50 mt-3">Amount: ${(status.amount_total / 100).toFixed(2)} {status.currency?.toUpperCase()}</p>
            <Link to="/" className="btn-primary-neon mt-6 inline-block">Back to home</Link>
          </>
        ) : (
          <>
            <div className="animate-pulseglow inline-block w-12 h-12 rounded-full bg-neonpink mb-4"/>
            <h1 className="font-display text-2xl font-black">Confirming your payment...</h1>
            <p className="text-white/60 text-sm mt-2">Status: {status.payment_status}</p>
            <p className="text-white/40 text-xs mt-2">Attempt {attempts + 1} / 8</p>
          </>
        )}
      </div>
    </div>
  );
}
